import { Component } from '@theme/component';
import { debounce, onAnimationEnd, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DrawerCloseEvent, DrawerOpenEvent, ThemeDrawer } from '@theme/theme-drawer';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [searchIcon] - The search icon, swapped out for the reset button.
 * @property {HTMLElement} [viewAllFooter] - The "view all results" footer, shown once there are results.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * The rendered empty state, kept for the life of the page. Popular searches and the empty-state
   * collection are fixed per render, and recently-viewed only changes by navigating (which reloads
   * this) or by the Clear button, which drops the cache. Reusing it turns clearing the search from
   * a round trip into a morph.
   * @type {string | null}
   */
  #emptyStateMarkup = null;

  /**
   * The drawer hosting the component. `snippets/search-modal.liquid` is its only host, and that is
   * a `theme-drawer` — a `dialog-component` lookup here silently matched nothing.
   * @returns {ThemeDrawer | null} The host drawer.
   */
  get dialog() {
    return /** @type {ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DrawerCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DrawerOpenEvent.eventName, this.#handleDialogOpen, {
        signal,
        once: true,
      });
    }

    // The empty state is fetched, not shipped in the page (`load_empty_state: false` in
    // `snippets/search-modal.liquid`), so warm it while the browser is idle. Opening the drawer
    // before this lands falls through to `#handleDialogOpen`.
    requestIdleCallback(() => {
      this.#loadEmptyState();
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input.
   * Bound declaratively by `on:click` in `snippets/search-modal.liquid`, not here.
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (!(event.metaKey || event.ctrlKey) || event.key !== 'k') return;

    // Chrome and Firefox bind Cmd/Ctrl+K to their own address bar.
    event.preventDefault();
    this.dialog?.toggle();
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    // Closing an untouched drawer changed nothing, so re-rendering the empty state would be two
    // section fetches for the same markup already on screen.
    if (!this.refs.searchInput.value) return;
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  get #showRecentlyViewed() {
    return this.dataset.showRecentlyViewed !== 'false';
  }

  get #hasRecentlyViewed() {
    return this.#showRecentlyViewed && RecentlyViewed.getProducts().length > 0;
  }

  /**
   * Prime the empty state, which is fetched rather than shipped in the page.
   *
   * Deliberately not the debounced `resetSearch`: that would clear the input a tick or two later,
   * wiping a query typed in between. Reading the input and acting on it in the same tick keeps
   * priming out of the shopper's way, and a query started mid-fetch aborts it anyway.
   */
  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    // Typing already beat us to it; clearing that query will fetch the empty state.
    if (this.refs.searchInput.value) return;

    this.#emptyStateLoaded = true;
    this.#resetSearch();
  }

  /**
   * The result links arrow keys move focus between, in document order. Recently-viewed items carry
   * their own ref because the Clear button needs them, so both are collected. Results that are on
   * screen but not visible don't count: tab panels hide inactive types, and the carousel layout
   * marks off-screen slides `aria-hidden`.
   * @returns {HTMLAnchorElement[]} The focusable result links.
   */
  get #resultLinks() {
    const selector = '[ref="resultsItems[]"] a[href], [ref="recentlyViewedItems[]"] a[href]';
    const links = Array.from(this.querySelectorAll(selector)).filter(
      (link) => !link.closest('[role="tabpanel"][hidden], [aria-hidden="true"]'),
    );

    return /** @type {HTMLAnchorElement[]} */ (links);
  }

  /**
   * Move focus by `step` through the result links, wrapping at the end. Stepping back off the
   * first link returns to the input rather than wrapping, so the search field is always one
   * ArrowUp away.
   * @param {number} step - How far to move, and in which direction.
   */
  #moveFocus(step) {
    const links = this.#resultLinks;
    if (!links.length) return;

    const currentIndex = links.indexOf(/** @type {HTMLAnchorElement} */ (document.activeElement));

    if (currentIndex === -1) {
      links[step > 0 ? 0 : links.length - 1]?.focus();
      return;
    }

    const nextIndex = currentIndex + step;

    if (nextIndex < 0) {
      this.refs.searchInput.focus();
      return;
    }

    links[nextIndex % links.length]?.focus();
  }

  /**
   * Arrow keys walk the results, Escape backs out one step at a time. Tab is left alone: focus is
   * real here, so the native tab order already walks the same links.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    switch (event.key) {
      case 'Escape':
        // Clear first, close second. Without this the drawer's document-level Escape handler
        // sees an unhandled event and closes on the same keypress.
        if (this.refs.searchInput.value) {
          event.preventDefault();
          this.#resetSearch();
          this.refs.searchInput.focus();
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.#moveFocus(1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#moveFocus(-1);
        break;

      case 'Enter': {
        // One result across every enabled type: Enter goes straight there instead of to the
        // search page. Anything else is the form's own submit.
        const singleResult = this.refs.predictiveSearchResults.querySelector(
          '[data-single-result-url]',
        );

        if (
          document.activeElement === this.refs.searchInput &&
          singleResult instanceof HTMLElement &&
          singleResult.dataset.singleResultUrl
        ) {
          event.preventDefault();
          window.location.href = singleResult.dataset.singleResultUrl;
        }
        break;
      }

      default: {
        // Keep typing refining the query even after the arrows have moved focus into the results,
        // which is what the old virtual-focus model gave for free. Focusing during keydown lets
        // the character land in the input, so the keystroke is carried over rather than swallowed.
        const isPrintable =
          event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;
        // Only from a result link. Space is printable too, so redirecting from anywhere would
        // swallow it before it could activate a focused button — the tabs, Clear, View all.
        const onResultLink = this.#resultLinks.includes(
          /** @type {HTMLAnchorElement} */ (document.activeElement),
        );

        if (isPrintable && onResultLink) {
          const { searchInput } = this.refs;
          searchInput.focus();
          searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
        }
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();
    // The cached empty state still holds the list that was just cleared.
    this.#emptyStateMarkup = null;

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * The previous results stay on screen across an async swap, so mark them stale rather than
   * letting them read as current.
   * @param {boolean} isBusy - Whether a fetch is in flight.
   */
  #setBusy(isBusy) {
    this.refs.predictiveSearchResults.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults
        .querySelector('[data-search-results]')
        ?.scrollTo(0, 0);
      this.refs.predictiveSearchResults.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    // No `resources[type]`: the Search & Discovery app's "Predictive search results" setting decides
    // which types come back, so the merchant changes it in one place rather than two.
    const { resultsLimit } = this.dataset;
    if (resultsLimit) url.searchParams.set('resources[limit]', resultsLimit);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();
    this.#setBusy(true);

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      }),
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url, abortController.signal)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          this.#toggleViewAllFooter(false);
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);
        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount =
          predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        this.#toggleViewAllFooter(resultCount > 0);
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;

        // The previous query's results are still on screen and would read as an answer to this
        // one, so replace them rather than leaving a silently wrong result set up.
        this.#renderError();
      })
      .finally(() => {
        // An aborted fetch has already been superseded, so the newer one owns the busy state.
        if (!abortController.signal.aborted) this.#setBusy(false);
      });
  }

  /** Swap the results for a message, so a failed fetch never reads as an answer. */
  #renderError() {
    // Cloned from markup rather than built here, so the message's classes stay in the Liquid.
    const template = document.getElementById('predictive-search-error-template');
    if (!(template instanceof HTMLTemplateElement)) {
      throw new Error('predictive-search: #predictive-search-error-template is missing');
    }

    this.refs.predictiveSearchResults.replaceChildren(template.content.cloneNode(true));
    this.#toggleViewAllFooter(false);
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @param {AbortSignal} signal - Cancels the fetch when a newer swap supersedes it.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup(signal) {
    if (!this.dataset.sectionId || !this.#showRecentlyViewed) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set(
      'q',
      viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '),
    );
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, signal);
  }

  /**
   * @param {boolean} show - Whether the footer has results worth linking to.
   */
  #toggleViewAllFooter(show) {
    this.refs.viewAllFooter?.toggleAttribute('hidden', !show);
  }

  #hideResetButton() {
    const { resetButton, searchIcon } = this.refs;

    resetButton.hidden = true;
    searchIcon?.toggleAttribute('hidden', false);
  }

  #showResetButton() {
    const { resetButton, searchIcon } = this.refs;

    resetButton.hidden = false;
    searchIcon?.toggleAttribute('hidden', true);
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';
    const recentlyViewedId = 'predictive-search-recently-viewed';

    searchInput.value = '';
    this.#hideResetButton();
    this.#toggleViewAllFooter(false);

    const abortController = this.#createAbortController();
    this.#setBusy(true);

    try {
      await this.#renderEmptyState(emptySectionId, recentlyViewedId, abortController);
    } catch (error) {
      if (!abortController.signal.aborted) this.#renderError();
    } finally {
      if (!abortController.signal.aborted) this.#setBusy(false);
    }
  };

  /**
   * Swap the dropdown back to its pre-query state.
   * @param {string} emptySectionId - Section id for the empty state.
   * @param {string} recentlyViewedId - Id of the slot recently-viewed products are morphed into.
   * @param {AbortController} abortController - Controller for the in-flight swap.
   */
  #renderEmptyState = async (emptySectionId, recentlyViewedId, abortController) => {
    const { predictiveSearchResults } = this.refs;

    if (this.#emptyStateMarkup) {
      morph(predictiveSearchResults, this.#emptyStateMarkup);
      this.#resetScrollPositions();
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    // Two independent section renders. Awaiting them in sequence doubled how long the dropdown sat
    // dimmed showing the previous query's results — and whether there is anything recently viewed
    // is known synchronously, so the second fetch never needed the first one's markup.
    const [emptySectionMarkup, recentlyViewedMarkup] = await Promise.all([
      sectionRenderer.getSectionHTML(emptySectionId, false, url, abortController.signal),
      this.#hasRecentlyViewed
        ? this.#getRecentlyViewedProductsMarkup(abortController.signal)
        : null,
    ]);

    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('[data-search-results]')
      ?.closest('[id^="shopify-section-"]');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const slot = parsedEmptySectionMarkup.querySelector(`#${recentlyViewedId}`);

    if (slot && recentlyViewedMarkup) {
      const recentlyViewedList = new DOMParser()
        .parseFromString(recentlyViewedMarkup, 'text/html')
        .getElementById(recentlyViewedId);

      // Nothing to show is not a reason to skip the empty state, so the swap stays optional.
      if (recentlyViewedList) slot.replaceWith(recentlyViewedList);
    }

    if (abortController.signal.aborted) return;

    this.#emptyStateMarkup = parsedEmptySectionMarkup.outerHTML;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
